import { FC } from "react";
import { useTranslation } from "react-i18next";

import { findBrandByDomain } from "../../../configs/configLoader";
import {
  policityStyles,
  titleStyles,
  uxGender,
  welcomeScreenUI,
} from "../../../configs";
import { useIsMobile } from "../../../utils";
import { isChildren, isFootwear } from "../../../utils/categoryParams";
import { getFileFromCloud, MODAL_STEPS } from "../../../constants/modal";

import "./index.css";

interface IPropsMeasuredBy {
  step: {
    number: number;
  } | null;
}

const MeasuredBy: FC<IPropsMeasuredBy> = ({ step }) => {
  const { t } = useTranslation("components/intro");

  const isMobile = useIsMobile();


  const brandDefined = findBrandByDomain();

  const isNinaRicci = brandDefined?.name === "Nina Ricci";
  const isLTDC = brandDefined?.name === "LTDC";
  const isSRP = brandDefined?.name === "SRP";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isKENZO = brandDefined?.name === "KENZO";
  const isTDF = brandDefined?.name === "TDF";

  const isGivenchy = brandDefined?.name === "Givenchy";
  const isGivenchyMobile = isGivenchy && isMobile;
  const isGivenchyDesktop = isGivenchy && !isMobile;

  const isNinaRicciFirstScreen =
    isNinaRicci &&
    (step?.number === MODAL_STEPS.INTRO.number ||
      step?.number === MODAL_STEPS.GENDER.number ||
      step?.number === MODAL_STEPS.INTRO_MOBILE.number);

  const customLinkStyles = {
    fontSize: policityStyles.fontSize,
    color: policityStyles.fontColor,
    fontWeight: policityStyles.fontWeight,
    textTransform: policityStyles.textTransform,
    letterSpacing: (policityStyles as any)?.letterSpacing,
  };

  const handlePoliticBlock = () => {
    const isShowBlock =
      (!isMobile
        ? step?.number === MODAL_STEPS.INTRO.number
        : !uxGender
          ? step?.number === MODAL_STEPS.GENDER.number
          : step?.number === MODAL_STEPS.INTRO_MOBILE.number) ||
      (isFootwear && step?.number === MODAL_STEPS.DEVICE_SELECT.number) ||
      isGivenchy ||
      (isTDF && step?.number === MODAL_STEPS.INTRO_MOBILE.number);

    const rules = [
      { when: isGivenchyDesktop, value: "105px" },
      { when: isMobile && isSRP, value: "30px" },
      { when: isMobile && isVB, value: "5px" },
      { when: isMobile && !isVB, value: "0px" },
    ];

    const bottomStyle = (rules.find((r) => r.when) || { value: "45px" }).value;

    return (
      <>
        {isShowBlock ? (
          <div
            tabIndex={0}
            className="policy"
            style={{
              display: "flex",
              justifyContent: isGivenchy ? "flex-start" : "center",
              width: isMobile ? "100%" : "calc(100% - 80px)",
              position: isMobile ? "relative" : "fixed",
              bottom: bottomStyle,
              color: customLinkStyles.color,
              fontSize: isMobile ? "10px" : customLinkStyles.fontSize,
              zIndex: 999999,
              fontStyle: "normal",
              outline: "none",
              marginTop: isGivenchyMobile ? "20px" : 0,
              letterSpacing: (titleStyles as any)?.letterSpacing,
            }}
            dangerouslySetInnerHTML={{
              __html: t("pp_link")
                .replace(/<a(.*?)>/g, (match: any) => {
                  return `<a style="${Object.entries(customLinkStyles)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join("; ")}" target="_blank"${match.slice(2)}`;
                })
                .replace(/<\/a>\./g, ".</a>"),
            }}
          />
        ) : null}
      </>
    );
  };

  const handleMeasuredByBlock = () => {
    return (
      <div
        className="measured-by"
        style={{
          position: isGivenchy ? "relative" : isMobile ? "relative" : "fixed",
          bottom: isGivenchyDesktop ? "65px" : 0,
          top: "inherit",
          color: isKENZO
            ? welcomeScreenUI.genderCTA.focused.backgroundColor
            : isSRP
              ? "#767474"
              : customLinkStyles.color,
          fontSize: isMobile ? "10px" : customLinkStyles.fontSize,
          fontWeight: "500",
          zIndex: 9999,
          height: isMobile ? "20px" : "auto",
          left: isGivenchy ? "" : "54%",
          display: "flex",
          justifyContent: isGivenchy ? "flex-start" : "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: isMobile ? "10px" : customLinkStyles.fontSize,
            fontStyle: "normal",
          }}
        >
          {t("measured_by") !== "measured_by"
            ? t("measured_by")
            : "Measured by"}
          <img
            src={getFileFromCloud("assets/brands/KLEEP_Black_Logo.png")}
            alt="KLEEP_Logo"
            style={{
              width: "80px",
              height: "auto",
              display: "block",
              objectFit: "contain",
              position: "relative",
              right: "20px",
              bottom: "1px",
              zIndex: 999999,
            }}
          />
        </div>
      </div>
    );
  };

  return isChildren ? null : (
    <>
      {!isMobile && isLTDC && step?.number !== MODAL_STEPS.INTRO.number && (
        <div
          style={{
            position: "absolute",
            bottom: "50px",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <img
            src={getFileFromCloud("assets/brands/logo-LTDS.png")}
            alt="LTDC logo"
            style={{
              width: "200px",
              height: "auto",
            }}
          />
        </div>
      )}
      {!isNinaRicciFirstScreen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            position: isGivenchyDesktop ? "fixed" : "relative",
            bottom: "0",
            left: isGivenchyDesktop ? "80px" : "",
            width: isMobile ? "100%" : "calc(100% - 120px)",
          }}
        >
          {handlePoliticBlock()}
          {handleMeasuredByBlock()}
        </div>
      )}
    </>
  );
};

export default MeasuredBy;
